import { Extension } from "@tiptap/react";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { discardPendingFiles, enqueuePendingFiles } from "./pending-files-queue";

export interface ImagePasteDropOptions {
  accept?: string;
  limit?: number;
  maxSize?: number;
  uploadNodeName?: string;
  onError?: (error: Error) => void;
}

const PLUGIN_KEY = new PluginKey("imagePasteDrop");

function matchesAccept(file: File, accept: string | undefined | null): boolean {
  if (!accept || accept === "*" || accept === "*/*") return true;
  const fileType = (file.type || "").toLowerCase();
  const fileName = (file.name || "").toLowerCase();
  return accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token === "*/*") return true;
      if (token.startsWith(".")) return fileName.endsWith(token);
      if (token.endsWith("/*")) return fileType.startsWith(token.slice(0, -1));
      return fileType === token;
    });
}

function collectImageFiles(
  dataTransfer: DataTransfer | null | undefined,
  options: ImagePasteDropOptions,
): File[] {
  if (!dataTransfer) return [];

  const accept = options.accept ?? "image/*";
  const maxSize = options.maxSize;
  const onError = options.onError;

  const candidates: File[] = [];

  if (dataTransfer.items && dataTransfer.items.length > 0) {
    for (const item of Array.from(dataTransfer.items)) {
      if (item && item.kind === "file") {
        const file = item.getAsFile();
        if (file) candidates.push(file);
      }
    }
  }

  if (candidates.length === 0 && dataTransfer.files && dataTransfer.files.length > 0) {
    for (const file of Array.from(dataTransfer.files)) {
      if (file) candidates.push(file);
    }
  }

  const accepted: File[] = [];
  for (const file of candidates) {
    if (!matchesAccept(file, accept)) continue;
    if (typeof maxSize === "number" && maxSize > 0 && file.size > maxSize) {
      onError?.(
        new Error(
          `File "${file.name}" (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the ${maxSize / 1024 / 1024}MB limit`,
        ),
      );
      continue;
    }
    accepted.push(file);
  }

  if (typeof options.limit === "number" && options.limit > 0) {
    return accepted.slice(0, options.limit);
  }
  return accepted;
}

function isInsideImageUpload(target: EventTarget | null): boolean {
  if (!target || !(target instanceof Element)) return false;
  return Boolean(
    target.closest('[data-type="image-upload"]') ||
      target.closest(".tiptap-image-upload") ||
      target.closest(".tiptap-image-upload-drag-area"),
  );
}

export const ImagePasteDrop = Extension.create<ImagePasteDropOptions>({
  name: "imagePasteDrop",

  addOptions() {
    return {
      accept: "image/*",
      limit: undefined,
      maxSize: undefined,
      uploadNodeName: "imageUpload",
      onError: undefined,
    };
  },

  addProseMirrorPlugins() {
    const ext = this;

    // Pulled out of insertUploadNode so paste/drop can short-circuit BEFORE
    // calling preventDefault — otherwise, if the schema lacks the upload
    // node we'd swallow the event and the paste would silently disappear.
    function canInsertUploadNode(view: EditorView): boolean {
      const nodeName = ext.options.uploadNodeName ?? "imageUpload";
      return Boolean(view.state.schema.nodes[nodeName]);
    }

    function insertUploadNode(view: EditorView, pos: number | undefined, files: File[]): boolean {
      const nodeName = ext.options.uploadNodeName ?? "imageUpload";
      if (!view.state.schema.nodes[nodeName]) return false;

      const pendingId = enqueuePendingFiles(files);
      let inserted = false;

      try {
        const editor = ext.editor;
        if (editor) {
          const chain = editor.chain().focus();
          if (typeof pos === "number") {
            chain.insertContentAt(pos, {
              type: nodeName,
              attrs: { pendingFilesId: pendingId },
            });
          } else {
            chain.insertContent({
              type: nodeName,
              attrs: { pendingFilesId: pendingId },
            });
          }
          inserted = chain.run();
        } else {
          const nodeType = view.state.schema.nodes[nodeName];
          const node = nodeType.create({ pendingFilesId: pendingId });
          const safePos =
            typeof pos === "number"
              ? Math.max(0, Math.min(pos, view.state.doc.content.size))
              : view.state.selection.from;
          view.dispatch(view.state.tr.insert(safePos, node));
          inserted = true;
        }
      } catch {
        inserted = false;
      }

      if (!inserted) discardPendingFiles(pendingId);
      return inserted;
    }

    return [
      new Plugin({
        key: PLUGIN_KEY,
        props: {
          handlePaste(view, event) {
            if (!view.editable) return false;
            if (isInsideImageUpload(event.target)) return false;
            if (!canInsertUploadNode(view)) return false;

            const files = collectImageFiles(event.clipboardData, ext.options);
            if (files.length === 0) return false;

            event.preventDefault();
            return insertUploadNode(view, undefined, files);
          },

          handleDrop(view, event, _slice, moved) {
            if (!view.editable) return false;
            if (moved) return false;
            if (isInsideImageUpload(event.target)) return false;
            if (!canInsertUploadNode(view)) return false;

            const files = collectImageFiles(event.dataTransfer, ext.options);
            if (files.length === 0) return false;

            event.preventDefault();
            const coords = view.posAtCoords({
              left: event.clientX,
              top: event.clientY,
            });
            return insertUploadNode(view, coords?.pos, files);
          },
        },
      }),
    ];
  },
});
